import React, { useState, useCallback, useEffect, useRef } from 'react';
import './BatchProcessingContainer.css';

// 단계별 컴포넌트들
import Step1ExcelUpload, { BATCH_SUPPORTED_CATEGORIES } from './batch/Step1_ExcelUpload';
// Step4·Step5·검사기가 같이 쓰는 단계 머리말·이전/다음 버튼. 뒤 파일이 앞 파일을 덮으므로 순서를 지킨다.
import './batch/BatchStepShared.css';
import './batch/BatchStepNavigation.css';
import Step4Processing from './batch/Step4_Processing';
import Step5Results from './batch/Step5_Results';

const createDefaultSelectedOptions = () => ({
  sections: {
    haengbal: true,
    seteuk: { enabled: true, subjects: [] },
    changche: true
  },
  settings: {
    tone: 'formal',
    length: 'medium',
    focus: 'balanced'
  }
});

const BatchProcessingContainer = ({ 
  onBack, 
  currentStep, 
  setCurrentStep,
  batchData,
  setBatchData,
  selectedModel,  // 🆕 선택된 모델
  isProUser,      // 🆕 PRO 사용자 여부
  onShowProModal, // 🆕 PRO 모달 표시 콜백
  initialBatchState,
  onResetSession,
  resetToken = 'initial',
  onCategoryChange,  // 🎨 Step1 에서 고른 생기부 종류를 상위(내 스타일 표시)로 올린다
  initialCategory = null, // 단일 생성에서 쓰던 종류를 이어받아 첫 진입 기본 탭으로 쓴다 (지원 종류가 아니면 무시)
}) => {
  // 각 단계별 데이터
  const [uploadedExcel, setUploadedExcel] = useState(null);
  const [analyzedData, setAnalyzedData] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(createDefaultSelectedOptions);
  const [processingResults, setProcessingResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Step1 의 설정 스냅샷(종류·바이트·교사 지시·참고자료·성취기준·학교급 세그먼트).
  // Step1 은 2단계부터 언마운트되므로 여기서 들고 있다가 결과 단계에서 돌아올 때 그대로 돌려준다.
  // 새 진입 토큰(resetToken)에서만 비운다.
  const [persistedBatchOptions, setPersistedBatchOptions] = useState(null);
  // 첫 진입(스냅샷 없음)이면 단일 생성에서 쓰던 종류로 시작한다. 프로덕션 분석: 세션 내 단일→다중 전환 860건,
  // 다중 기본값(행동발달)은 실제 첫 선택의 9.6%에 불과했다.
  const seededBatchOptions = BATCH_SUPPORTED_CATEGORIES.includes(initialCategory)
    ? { options: { category: initialCategory }, seedOnly: true }
    : null;
  const previousResetTokenRef = useRef(resetToken);
  const isInitialMountRef = useRef(true);
  const isResetPending = isInitialMountRef.current || previousResetTokenRef.current !== resetToken;

  useEffect(() => {
    if (!isResetPending) return;
    isInitialMountRef.current = false;
    previousResetTokenRef.current = resetToken;
    setUploadedExcel(null);
    setAnalyzedData(null);
    setSelectedOptions(createDefaultSelectedOptions());
    setProcessingResults(null);
    setIsProcessing(false);
    setPersistedBatchOptions(null);
    setBatchData?.(null);
    setCurrentStep(1);
  }, [isResetPending, resetToken, setBatchData, setCurrentStep]);

  // 단계 이동 핸들러
  const handleNextStep = useCallback((stepData) => {
    switch (currentStep) {
      case 1: // 엑셀 업로드 완료
        setUploadedExcel(stepData);
        // skipToResults 플래그가 있으면 바로 Step 5(결과)로 이동
        if (stepData.skipToResults) {
          setProcessingResults({
            ...(stepData.results || {}),
            record_id: stepData.recordId || stepData.results?.record_id,
            history_saved: stepData.historySaved ?? stepData.results?.history_saved,
            history_error: stepData.historyError || stepData.results?.history_error,
          });
          setCurrentStep(5);
        } else {
          // 학생 확인은 Step1 안에서 끝나므로 바로 생성으로 간다.
          setAnalyzedData(stepData);
          setSelectedOptions(stepData.selectedOptions || createDefaultSelectedOptions());
          setCurrentStep(4);
        }
        break;
      case 4: // 처리 완료
        setProcessingResults(stepData);
        setCurrentStep(5);
        break;
      default:
        break;
    }
  }, [currentStep, setCurrentStep]);

  // 생성 중단·실패 뒤 "이전"은 입력 화면으로. uploadedExcel 을 남겨 Step1 이 학생 목록까지 복원한다.
  const handlePrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(1);
    } else {
      onBack();
    }
  }, [currentStep, setCurrentStep, onBack]);

  // 단계별 컴포넌트 렌더링
  const renderCurrentStep = () => {
    const renderedStep = isResetPending ? 1 : currentStep;
    switch (renderedStep) {
      case 1:
        return (
          <Step1ExcelUpload
            onNext={handleNextStep}
            onBack={handlePrevStep}
            uploadedData={isResetPending ? null : uploadedExcel}
            initialBatchState={initialBatchState}
            onCategoryChange={onCategoryChange}
            persistedOptions={isResetPending ? seededBatchOptions : (persistedBatchOptions || seededBatchOptions)}
            onOptionsPersist={setPersistedBatchOptions}
          />
        );
      case 4:
        return (
          <Step4Processing
            analyzedData={analyzedData}
            selectedOptions={selectedOptions}
            onNext={handleNextStep}
            onBack={handlePrevStep}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
          />
        );
      case 5:
        return (
          <Step5Results
            processingResults={processingResults}
            onReplaceResults={setProcessingResults}
            // 입력으로 돌아가기: uploadedExcel 을 남겨 Step1 이 학생 목록까지 복원한다
            onBack={() => setCurrentStep(1)}
            // 다음 학급 만들기: 학생 자료·결과만 비우고 설정(persistedBatchOptions)은 유지
            onStartNew={() => {
              onResetSession?.();
              setCurrentStep(1);
              setUploadedExcel(null);
              setAnalyzedData(null);
              setProcessingResults(null);
            }}
            // 설정도 초기화하고 새로 시작
            onResetAll={() => {
              onResetSession?.();
              setPersistedBatchOptions(null);
              setCurrentStep(1);
              setUploadedExcel(null);
              setAnalyzedData(null);
              setProcessingResults(null);
            }}
            selectedModel={selectedModel}     // 🆕 모델 정보 전달
            isProUser={isProUser}             // 🆕 PRO 사용자 여부 전달
            onShowProModal={onShowProModal}   // 🆕 PRO 모달 콜백 전달
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="saenggibu-batch-processing-container">
      {/* 현재 단계 컨텐츠 */}
      <div className="saenggibu-batch-step-content" key={resetToken}>
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default BatchProcessingContainer;
